import { useQueryClient } from "@tanstack/react-query";
import { type MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { CampaignCreationInvalidProduct } from "@/lib/campaign-creation-session";
import type { SelectedProduct } from "@/lib/campaign-creation-state";
import { emitCampaignCreationTelemetry } from "@/lib/campaign-creation-telemetry";
import { fetchBulkProductListingIdentities } from "@/lib/product-listing-api";
import {
  matchesProductIssue,
  selectedProductFromListing,
  selectedSkuSetFingerprint,
} from "./product-listing-helpers";
import type { ProductListingBrowse } from "./use-product-listing-browse";

export type AddProductsMethod = "search" | "enter" | "bulk";

export function useMountedRef(): MutableRefObject<boolean> {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return mounted;
}

interface UseProductSelectionOptions {
  shopId: string;
  selectionLimit: number;
  selected: SelectedProduct[];
  onSelectedChange: (products: SelectedProduct[]) => void;
  enterInput: string;
  invalidProducts: CampaignCreationInvalidProduct[];
  onInvalidProductsChange: (products: CampaignCreationInvalidProduct[]) => void;
  onProductsAdded?: (method: AddProductsMethod, count: number) => void;
  mounted: MutableRefObject<boolean>;
  browse: Pick<
    ProductListingBrowse,
    | "search"
    | "availability"
    | "productStatus"
    | "filterSnapshot"
    | "listingAdProduct"
    | "bulkSelectable"
  >;
}

/**
 * Selected-SKU bookkeeping: add/remove/bulk-select actions, stale-action guards and the
 * read that intersects the selection with the current browse filters.
 */
export function useProductSelection({
  shopId,
  selectionLimit,
  selected,
  onSelectedChange,
  enterInput,
  invalidProducts,
  onInvalidProductsChange,
  onProductsAdded,
  mounted,
  browse: { search, availability, productStatus, filterSnapshot, listingAdProduct, bulkSelectable },
}: UseProductSelectionOptions) {
  const { t } = useTranslation("campaignCreation");
  const queryClient = useQueryClient();
  const [matchingSelected, setMatchingSelected] = useState<SelectedProduct[]>([]);
  const [matchingTotal, setMatchingTotal] = useState(0);
  const [matchingContext, setMatchingContext] = useState("");
  const [matchingErrorContext, setMatchingErrorContext] = useState("");
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const selectionIdentity = JSON.stringify([shopId, selected.map(({ sku, asin }) => [sku, asin])]);
  const selectionRevision = useRef({ identity: selectionIdentity, revision: 0 });
  if (selectionRevision.current.identity !== selectionIdentity) {
    selectionRevision.current = {
      identity: selectionIdentity,
      revision: selectionRevision.current.revision + 1,
    };
  }
  const actionVersion = useRef(0);
  const bulkInFlight = useRef(false);
  const [bulkPending, setBulkPending] = useState(false);
  const actionContext = JSON.stringify([
    shopId,
    search,
    availability,
    productStatus,
    enterInput,
    selected,
  ]);
  const previousActionContext = useRef(actionContext);
  if (previousActionContext.current !== actionContext) {
    actionVersion.current += 1;
    previousActionContext.current = actionContext;
  }

  const selectedSkus = useMemo(() => selected.map((product) => product.sku), [selected]);
  const matchContext = JSON.stringify([shopId, filterSnapshot, selectedSkus]);
  const matchingCurrent = selectedSkus.length === 0 || matchingContext === matchContext;
  // The intersection read only decorates checkbox state; when it fails, keep bulk selection
  // usable (it performs its own read) instead of waiting for a result that will not arrive.
  const matchingSettled = matchingCurrent || matchingErrorContext === matchContext;
  useEffect(() => {
    if (selectedSkus.length === 0) {
      setMatchingSelected([]);
      setMatchingTotal(0);
      setMatchingContext(matchContext);
      return;
    }
    if (!shopId) return;
    let obsolete = false;
    void fetchBulkProductListingIdentities(shopId, {
      mode: "intersect",
      filters: filterSnapshot,
      selectedSkus,
    })
      .then((result) => {
        if (obsolete) return;
        setMatchingSelected(result.identities.map(selectedProductFromListing));
        setMatchingTotal(result.totalMatching);
        setMatchingContext(matchContext);
      })
      .catch(() => {
        if (!obsolete) setMatchingErrorContext(matchContext);
      });
    return () => {
      obsolete = true;
    };
  }, [filterSnapshot, matchContext, selectedSkus, shopId]);

  const selectedBySku = useMemo(
    () => new Map(selected.map((product) => [product.sku, product])),
    [selected],
  );
  const selectedForDisplay = useMemo(() => {
    const refreshedBySku = new Map(matchingSelected.map((product) => [product.sku, product]));
    return selected.map((product) => {
      const refreshed = refreshedBySku.get(product.sku);
      if (!refreshed) return product;
      return {
        ...product,
        ...refreshed,
        title: refreshed.title ?? product.title,
        imageUrl: refreshed.imageUrl ?? product.imageUrl,
      };
    });
  }, [matchingSelected, selected]);

  function addProducts(products: SelectedProduct[], method: AddProductsMethod) {
    if (method !== "bulk" && bulkInFlight.current) return;
    actionVersion.current += 1;
    if (selectionLimit === 1 && products.length === 1) {
      const next = [products[0]!];
      selectedRef.current = next;
      onSelectedChange(next);
      if (onProductsAdded) onProductsAdded(method, 1);
      else
        emitCampaignCreationTelemetry({
          name: "products_added",
          step: 2,
          method,
          count: 1,
        });
      return;
    }
    const merged = new Map(selectedRef.current.map((product) => [product.sku, product]));
    for (const product of products) {
      if (!merged.has(product.sku)) merged.set(product.sku, product);
    }
    if (merged.size > selectionLimit) {
      toast.error(
        t("products.limit", {
          attempted: merged.size,
          max: selectionLimit,
        }),
      );
      return;
    }
    const next = [...merged.values()];
    selectedRef.current = next;
    onSelectedChange(next);
    if (onProductsAdded) onProductsAdded(method, products.length);
    else
      emitCampaignCreationTelemetry({
        name: "products_added",
        step: 2,
        method,
        count: products.length,
      });
  }
  function removeSkus(skus: Iterable<string>) {
    actionVersion.current += 1;
    const removing = new Set(skus);
    const next = selectedRef.current.filter((product) => !removing.has(product.sku));
    selectedRef.current = next;
    onInvalidProductsChange(
      invalidProducts.filter((product) =>
        next.some((selected) => matchesProductIssue(product, selected)),
      ),
    );
    onSelectedChange(next);
  }
  function removeAll() {
    removeSkus(selectedRef.current.map(({ sku }) => sku));
  }

  async function selectFiltered(variationGroupKey?: string) {
    if (bulkInFlight.current || !bulkSelectable) return;
    bulkInFlight.current = true;
    setBulkPending(true);
    try {
      await selectFilteredProducts(variationGroupKey);
    } catch {
      if (mounted.current) toast.error(t("products.loadError"));
    } finally {
      bulkInFlight.current = false;
      if (mounted.current) setBulkPending(false);
    }
  }
  async function selectFilteredProducts(variationGroupKey?: string) {
    const version = actionVersion.current;
    const selectedSkus = selectedRef.current.map((product) => product.sku);
    const selectedSkuFingerprint = await selectedSkuSetFingerprint(selectedSkus);
    if (!mounted.current || version !== actionVersion.current) return;
    const result = await queryClient.fetchQuery({
      queryKey: [
        "product-listings",
        "bulk-identities",
        "select",
        shopId,
        filterSnapshot,
        variationGroupKey ?? null,
        selectedSkuFingerprint,
      ],
      queryFn: () =>
        fetchBulkProductListingIdentities(shopId, {
          mode: "select",
          filters: filterSnapshot,
          variationGroupKey,
          selectedSkus,
        }),
      staleTime: 10_000,
    });
    if (!mounted.current || version !== actionVersion.current) return;
    if (result.overflow) {
      toast.error(
        t("products.limit", {
          attempted: result.resultingCount,
          max: selectionLimit,
        }),
      );
      return;
    }
    if (listingAdProduct === "sp") {
      void queryClient.invalidateQueries({ queryKey: ["product-listings", "groups", shopId] });
      void queryClient.invalidateQueries({ queryKey: ["product-listings", "list", shopId] });
    }
    addProducts(result.identities.map(selectedProductFromListing), "bulk");
  }

  return {
    selected,
    selectedRef,
    selectionRevision,
    actionVersion,
    selectedBySku,
    selectedForDisplay,
    matchingSelected,
    matchingTotal,
    matchingCurrent,
    matchingSettled,
    bulkPending,
    addProducts,
    removeSkus,
    removeAll,
    selectFiltered,
  };
}

export type ProductSelection = ReturnType<typeof useProductSelection>;
